'use client'

import { PropsWithChildren, useLayoutEffect, useRef, useState } from 'react'

import { cn, passPropsToChildren } from '@/shared/lib'
import { Icon, TextButton } from '@/shared/ui'

type ExpandableTextProps = {
  content: string
  lineClamp?: 1 | 2 | 3 | 4 | 5 | 6
  className?: string
}

export const ExpandableText = ({
  content,
  lineClamp = 5,
  className,
}: ExpandableTextProps) => {
  const textRef = useRef<HTMLParagraphElement>(null)

  const [isOverflown, setIsOverflown] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)

  useLayoutEffect(() => {
    if (textRef.current) {
      const element = textRef.current

      const lineHeight = parseInt(getComputedStyle(element).lineHeight)
      const maxHeight = lineHeight * lineClamp

      setIsOverflown(element.scrollHeight > maxHeight)
    }
  }, [lineClamp])

  const handleExpandButtonClick = () => {
    setIsExpanded(prevIsExpanded => !prevIsExpanded)
  }

  return (
    <>
      <p
        className={cn(
          'typo-body2-reading font-normal whitespace-break-spaces text-gray-900',
          {
            'line-clamp-1': lineClamp === 1,
            'line-clamp-2': lineClamp === 2,
            'line-clamp-3': lineClamp === 3,
            'line-clamp-4': lineClamp === 4,
            'line-clamp-5': lineClamp === 5,
            'line-clamp-6': lineClamp === 6,
          },
          isExpanded && 'line-clamp-none',
          className,
        )}
        ref={textRef}
      >
        {content}
      </p>

      {isOverflown && (
        <div className="flex justify-center">
          <ExpandableButton
            isExpanded={isExpanded}
            onClick={handleExpandButtonClick}
          />
        </div>
      )}
    </>
  )
}

const GradientBackground = {
  background:
    'linear-gradient(180deg, rgba(255, 255, 255, 0.00) 10%, rgba(255, 255, 255, 1) 72.61%)',
}

type ExpandableBoxProps = {
  maxHeight: number
  expandable?: boolean
}

export const ExpandableBox = ({
  maxHeight,
  expandable = false,
  children,
}: PropsWithChildren<ExpandableBoxProps>) => {
  const [isExpanded, setIsExpanded] = useState(false)

  const handleExpandButtonClick = () => {
    setIsExpanded(prevIsExpanded => !prevIsExpanded)
  }

  return (
    <div className="relative">
      {passPropsToChildren(children, {
        ...(expandable && {
          style: {
            maxHeight,
            overflow: 'hidden',
            ...(isExpanded && { marginBottom: '36px', maxHeight: 'none' }),
          },
        }),
      })}

      {expandable && (
        <div
          className={cn(
            'absolute -bottom-1 left-0 flex h-20 w-full items-end justify-center',
            isExpanded && '-bottom-9',
          )}
          {...(!isExpanded && { style: GradientBackground })}
        >
          <ExpandableButton
            isExpanded={isExpanded}
            onClick={handleExpandButtonClick}
          />
        </div>
      )}
    </div>
  )
}

type ExpandableButtonProps = {
  isExpanded: boolean
  onClick: () => void
}

export const ExpandableButton = ({
  isExpanded,
  onClick,
}: ExpandableButtonProps) => {
  return (
    <TextButton
      color="gray"
      size="large"
      className="h-fit gap-0"
      onClick={onClick}
    >
      {isExpanded ? '접기' : '더보기'}
      <Icon
        name="arrow-bottom"
        size={16}
        strokeWidth={1.5}
        className={cn(
          isExpanded && 'rotate-180 transition-transform duration-300',
        )}
      />
    </TextButton>
  )
}
